import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { bookingAPI } from '../lib/api';
import { 
  CheckCircle, Loader2, Calendar, Clock, Users, MapPin, Mail, Phone,
  PartyPopper, Download, Share2, Star, ArrowRight, MessageCircle
} from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import confetti from 'canvas-confetti';

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { settings } = useSettings();

  useEffect(() => {
    const checkBooking = async () => {
      if (!bookingId) {
        setError('No booking ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await bookingAPI.getStatus(bookingId);
        setBooking(response.data);
        
        // Trigger confetti when payment is confirmed
        if (response.data.payment_status === 'paid') {
          triggerConfetti();
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    checkBooking();
    
    // Poll for status updates if payment is pending
    const interval = setInterval(async () => {
      if (bookingId) {
        try {
          const response = await bookingAPI.getStatus(bookingId);
          setBooking(response.data);
          if (response.data.payment_status === 'paid') {
            triggerConfetti();
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [bookingId]);

  const triggerConfetti = () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 }
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio)
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const shareBooking = () => {
    if (navigator.share) {
      navigator.share({
        title: `My ${booking?.tour_title} Booking`,
        text: `I just booked ${booking?.tour_title} in Puerto Rico! Can't wait!`,
        url: window.location.href
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-green-50 to-white" data-testid="booking-success-loading">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
            <p className="text-xl text-muted-foreground">Processing your booking...</p>
            <p className="text-sm text-muted-foreground mt-2">Please don't close this page</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="booking-success-error">
        <Navbar />
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md p-8 text-center">
            <div className="text-red-500 text-6xl mb-4">&#9888;</div>
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>Return to Home</Button>
            </Link>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const isPaid = booking?.payment_status === 'paid';

  return (
    <div className="min-h-screen flex flex-col" data-testid="booking-success-page">
      <Navbar />
      
      <main className="flex-1 py-8 md:py-12 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-3xl mx-auto px-4">
          {/* Thank You Header */}
          <div className="text-center mb-8">
            {isPaid ? (
              <>
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mx-auto shadow-lg">
                    <CheckCircle className="h-14 w-14 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2">
                    <PartyPopper className="h-8 w-8 text-yellow-500 animate-bounce" />
                  </div>
                </div>
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-green-600 mb-3" data-testid="thank-you-title">
                  Thank You!
                </h1>
                <p className="text-xl text-gray-600 max-w-md mx-auto">
                  Your adventure awaits! We can't wait to see you.
                </p>
              </>
            ) : (
              <>
                <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Processing Payment...</h1>
                <p className="text-muted-foreground">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}
          </div>

          {/* Booking Confirmation Card */}
          {booking && isPaid && (
            <Card className="p-6 md:p-8 rounded-2xl shadow-xl border-t-4 border-t-green-500" data-testid="booking-confirmation-card">
              {/* Confirmation Badge */}
              <div className="flex items-center justify-center gap-2 bg-green-100 text-green-700 py-2 px-4 rounded-full w-fit mx-auto mb-6">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">Booking Confirmed</span>
              </div>

              {/* Tour Details */}
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2" data-testid="tour-title">
                  {booking.tour_title}
                </h2>
                <p className="text-muted-foreground">
                  Confirmation email sent to {booking.booker_email}
                </p>
              </div>

              {/* Booking Details Grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-semibold text-foreground">{formatDate(booking.date)}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Time</p>
                      <p className="font-semibold text-foreground">{booking.time}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Guests</p>
                      <p className="font-semibold text-foreground">{booking.guests} {booking.guests === 1 ? 'Guest' : 'Guests'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Booked By</p>
                      <p className="font-semibold text-foreground">{booking.booker_name}</p>
                      <p className="text-sm text-muted-foreground">{booking.booker_email}</p>
                    </div>
                  </div>

                  {booking.booker_phone && (
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-semibold text-foreground">{booking.booker_phone}</p>
                      </div>
                    </div>
                  )}

                  {booking.pickup_hotel && (
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Pickup Location</p>
                        <p className="font-semibold text-foreground">{booking.pickup_hotel}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Total Paid */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-8 text-center border border-green-100">
                <p className="text-sm text-green-700 mb-1">Total Paid</p>
                <p className="text-4xl font-bold text-green-600">${booking.total_price}</p>
              </div>

              {/* Guest List */}
              {booking.guest_info && booking.guest_info.length > 0 && (
                <div className="border-t pt-6 mb-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Guest List
                  </h3>
                  <div className="grid gap-2">
                    {booking.guest_info.map((guest, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="font-medium">{guest.full_name}</span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full ml-auto">
                          Waiver Signed
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Important Information */}
              <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
                <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Important Information
                </h3>
                <ul className="space-y-2 text-amber-800">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">&#8226;</span>
                    <span>Please arrive <strong>15 minutes before</strong> your scheduled time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">&#8226;</span>
                    <span>Bring comfortable shoes, sunscreen, and a hat</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">&#8226;</span>
                    <span>A reminder will be sent <strong>24 hours before</strong> your tour</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">&#8226;</span>
                    <span>Questions? Contact us at <strong>{settings?.email || 'support@tourfinder.pr'}</strong></span>
                  </li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
                <Link to="/">
                  <Button variant="outline" className="w-full sm:w-auto rounded-full px-6" data-testid="return-home-btn">
                    Return to Home
                  </Button>
                </Link>
                <Link to="/tours">
                  <Button className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6" data-testid="browse-more-btn">
                    Browse More Tours
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Share & Contact */}
              <div className="flex justify-center gap-4 mt-6 pt-6 border-t">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={shareBooking}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
                {settings?.whatsapp_number && (
                  <a 
                    href={`https://wa.me/${settings.whatsapp_number}?text=Hi! I just booked ${booking.tour_title} for ${booking.date}. Booking ID: ${booking.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp Us
                    </Button>
                  </a>
                )}
              </div>
            </Card>
          )}

          {/* Still Processing Card */}
          {booking && !isPaid && (
            <Card className="p-8 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Payment Processing</h2>
              <p className="text-muted-foreground mb-4">
                Your payment is being processed. This page will update automatically.
              </p>
              <p className="text-sm text-muted-foreground">
                Booking ID: {booking.id}
              </p>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
